import React, { useState, useEffect, useLayoutEffect, useRef } from 'react';
import { createPortal } from 'react-dom';
import { useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import {
  Bell, Check, X, Building2, CheckSquare, MessageSquare, AlertCircle, Clock, AtSign, FileSignature,
} from 'lucide-react';
import { formatDate } from '@/lib/formatters';
import {
  useMyNotifications, useMarkNotificationRead, useMarkAllNotificationsRead,
} from '@/hooks/useNotifications';
import { NDA_NOTIFICATION_TYPES } from '../../../supabase/functions/_shared/nda-status';

interface Notification {
  id: string;
  /** Deliberately open: the portal adds researcher_mention, property_proposal,
   *  edit_suggestion, contract_signed, portal_dnc, portal_feedback and more.
   *  A closed union here would have to be edited every time a producer adds a
   *  type, and would fail `npx tsc -b` when one did. */
  notification_type: string;
  title: string;
  message?: string | null;
  property_id?: string | null;
  entity_type?: string | null;
  entity_id?: string | null;
  action_url?: string | null;
  is_read: boolean | null;
  created_at: string;
}

interface NotificationsDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  triggerRef: React.RefObject<HTMLButtonElement>;
}

const notificationIcons: Record<string, React.ReactNode> = {
  task_assigned: <CheckSquare className="h-4 w-4" />,
  task_due: <AlertCircle className="h-4 w-4" />,
  property_update: <Building2 className="h-4 w-4" />,
  message: <MessageSquare className="h-4 w-4" />,
  system: <Bell className="h-4 w-4" />,
  // WS-G researcher portal
  researcher_mention: <AtSign className="h-4 w-4" />,
  property_proposal: <Building2 className="h-4 w-4" />,
  property_proposal_reviewed: <Building2 className="h-4 w-4" />,
  edit_suggestion: <CheckSquare className="h-4 w-4" />,
  edit_suggestion_reviewed: <CheckSquare className="h-4 w-4" />,
  contact_proposal: <MessageSquare className="h-4 w-4" />,
  contract_signed: <AlertCircle className="h-4 w-4" />,
  portal_dnc: <AlertCircle className="h-4 w-4" />,
  portal_feedback: <MessageSquare className="h-4 w-4" />,
  // WS-N/G development leads. `dev_status_changed` is the staff override — the
  // only notification on this book that reports a change the researcher did not
  // make themselves, which is exactly why it needs to reach them.
  dev_finding: <CheckSquare className="h-4 w-4" />,
  dev_finding_reviewed: <CheckSquare className="h-4 w-4" />,
  dev_status_changed: <AlertCircle className="h-4 w-4" />,
  // NDA e-signature (spec §5): every type the NDA writers use, from the one
  // vocabulary they write from — never re-typed here, so a type added there
  // gets its icon here without anybody remembering to. One icon for all of
  // them: the title already says which step it is.
  ...Object.fromEntries(NDA_NOTIFICATION_TYPES.map((t) => [t, <FileSignature key={t} className="h-4 w-4" />])),
};

/** A type this build has never heard of still gets a bell, not a blank. */
const iconFor = (t: string): React.ReactNode => notificationIcons[t] ?? <Bell className="h-4 w-4" />;

export function NotificationsDropdown({ isOpen, onClose, triggerRef }: NotificationsDropdownProps) {
  const navigate = useNavigate();
  const { data: notifications = [] } = useMyNotifications(30);
  const markReadMutation = useMarkNotificationRead();
  const markAllReadMutation = useMarkAllNotificationsRead();
  const panelRef = useRef<HTMLDivElement>(null);
  const [position, setPosition] = useState<{ top: number; left: number } | null>(null);

  useLayoutEffect(() => {
    if (!isOpen) {
      setPosition(null);
      return;
    }
    const triggerEl = triggerRef.current;
    const panelEl = panelRef.current;
    if (!triggerEl || !panelEl) return;
    // Viewport coordinates throughout: the panel is position: fixed, so adding
    // the page's scroll offset (as this once did) pushed it off-screen by
    // exactly as far as the page had been scrolled.
    const t = triggerEl.getBoundingClientRect();
    const p = panelEl.getBoundingClientRect();
    const margin = 8;
    setPosition({
      top: t.bottom + 8,
      left: Math.max(margin, Math.min(t.right - p.width, window.innerWidth - p.width - margin)),
    });
  }, [isOpen, triggerRef]);

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      if (
        triggerRef.current &&
        !triggerRef.current.contains(event.target as Node) &&
        !(event.target as Element).closest('[data-notifications-dropdown]')
      ) {
        onClose();
      }
    };

    const handleEscape = (event: KeyboardEvent) => {
      if (event.key === 'Escape') {
        onClose();
        triggerRef.current?.focus();
      }
    };

    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
      document.addEventListener('keydown', handleEscape);
      window.addEventListener('resize', onClose);
    }

    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
      document.removeEventListener('keydown', handleEscape);
      window.removeEventListener('resize', onClose);
    };
  }, [isOpen, onClose, triggerRef]);

  const handleNotificationClick = (notification: Notification) => {
    if (!notification.is_read) {
      markAsRead(notification.id);
    }
    if (notification.action_url) {
      navigate(notification.action_url);
      onClose();
    }
  };

  const markAsRead = (id: string) => {
    markReadMutation.mutate(id);
  };

  const markAllAsRead = () => {
    markAllReadMutation.mutate();
  };

  const unreadCount = notifications.filter((n) => !n.is_read).length;
  const unreadNotifications = notifications.filter((n) => !n.is_read);
  const readNotifications = notifications.filter((n) => n.is_read);

  if (!isOpen) return null;

  return createPortal(
    <div
      ref={panelRef}
      data-notifications-dropdown
      role="dialog"
      aria-label="Notifications"
      className={cn(
        'fixed z-[60] flex max-h-[min(600px,calc(100vh-5rem))] w-96 max-w-[calc(100vw-1rem)] flex-col overflow-hidden rounded-xl border border-gray-200 bg-white shadow-elevated dark:border-gray-700 dark:bg-gray-800',
        position && 'animate-fade-in'
      )}
      style={{
        top: position?.top ?? 0,
        left: position?.left ?? 0,
        visibility: position ? 'visible' : 'hidden',
      }}
    >
      {/* Header */}
      <div className="flex items-center justify-between px-4 py-3 border-b border-gray-100 dark:border-gray-700">
        <div className="flex items-center gap-2">
          <h3 className="text-sm font-semibold text-gray-900 dark:text-gray-100">Notifications</h3>
          {unreadCount > 0 && (
            <span className="px-2 py-0.5 bg-primary-100 dark:bg-primary-900/30 text-primary-700 dark:text-primary-300 text-xs font-medium tabular-nums rounded-full">
              {unreadCount}
            </span>
          )}
        </div>
        <div className="flex items-center gap-2">
          {unreadCount > 0 && (
            <button
              type="button"
              onClick={markAllAsRead}
              className="rounded text-xs text-primary-600 dark:text-primary-400 hover:text-primary-700 dark:hover:text-primary-300 font-medium"
            >
              Mark all read
            </button>
          )}
          <button
            type="button"
            onClick={onClose}
            aria-label="Close notifications"
            className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded-md hover:bg-gray-100 dark:hover:bg-gray-700"
          >
            <X className="h-4 w-4" />
          </button>
        </div>
      </div>

      {/* Notifications List */}
      <div className="flex-1 overflow-y-auto">
        {notifications.length === 0 ? (
          <div className="flex flex-col items-center justify-center py-12 px-4 text-center">
            <div className="mb-3 flex h-12 w-12 items-center justify-center rounded-xl bg-gray-100 ring-1 ring-inset ring-gray-200/70 dark:bg-gray-700 dark:ring-gray-600">
              <Bell className="h-6 w-6 text-gray-400 dark:text-gray-500" aria-hidden="true" />
            </div>
            <p className="text-sm font-medium text-gray-900 dark:text-gray-100">You&apos;re all caught up</p>
            <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
              Mentions, assignments and updates will appear here.
            </p>
          </div>
        ) : (
          <>
            {/* Unread Notifications */}
            {unreadNotifications.length > 0 && (
              <div className="py-2">
                {unreadNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className={cn(
                      'px-4 py-3 border-l-2 border-primary-500 bg-primary-50/50 dark:bg-primary-900/20 hover:bg-primary-50 dark:hover:bg-primary-900/30 cursor-pointer transition-colors',
                      notification.is_read && 'border-transparent bg-white dark:bg-gray-800'
                    )}
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex-shrink-0 text-primary-600 dark:text-primary-400">
                        {iconFor(notification.notification_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-2 mb-1">
                          <p className="text-sm font-medium text-gray-900 dark:text-gray-100">
                            {notification.title}
                          </p>
                          <button
                            type="button"
                            onClick={(e) => {
                              e.stopPropagation();
                              markAsRead(notification.id);
                            }}
                            aria-label="Mark as read"
                            title="Mark as read"
                            className="p-1 text-gray-400 dark:text-gray-500 hover:text-gray-600 dark:hover:text-gray-300 rounded flex-shrink-0 hover:bg-white dark:hover:bg-gray-700"
                          >
                            <Check className="h-3 w-3" />
                          </button>
                        </div>
                        {notification.message && (
                          <p className="text-sm text-gray-600 dark:text-gray-400 mb-1">{notification.message}</p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-gray-500 dark:text-gray-400">
                          <Clock className="h-3 w-3" />
                          {formatDate(notification.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}

            {/* Read Notifications */}
            {readNotifications.length > 0 && (
              <div className="py-2 border-t border-gray-100 dark:border-gray-700">
                <div className="px-4 py-2">
                  <p className="text-[10px] font-semibold uppercase tracking-widest text-gray-400 dark:text-gray-500">Earlier</p>
                </div>
                {readNotifications.map((notification) => (
                  <div
                    key={notification.id}
                    onClick={() => handleNotificationClick(notification)}
                    className="px-4 py-3 hover:bg-gray-50 dark:hover:bg-gray-700 cursor-pointer transition-colors"
                  >
                    <div className="flex items-start gap-3">
                      <div className="mt-0.5 flex-shrink-0 text-gray-400 dark:text-gray-500">
                        {iconFor(notification.notification_type)}
                      </div>
                      <div className="flex-1 min-w-0">
                        <p className="text-sm font-medium text-gray-700 dark:text-gray-300 mb-1">
                          {notification.title}
                        </p>
                        {notification.message && (
                          <p className="text-sm text-gray-500 dark:text-gray-400 mb-1">{notification.message}</p>
                        )}
                        <div className="flex items-center gap-2 text-xs text-gray-400 dark:text-gray-500">
                          <Clock className="h-3 w-3" />
                          {formatDate(notification.created_at)}
                        </div>
                      </div>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </>
        )}
      </div>

      {/* No "View all" footer: it linked to /notifications, which is not a
          route — the catch-all quietly sent it to the dashboard. The list
          above already holds everything the hook fetches. */}
    </div>,
    document.body
  );
}
